import type { Settings } from '@/components/posture-analysis/types';
import { DEFAULT_POSTURE_ORIENTATION_STRATEGY_ID } from '@/lib/posture-analysis/posture-pic-evaluation/strategies/posture-orientation-strategy-id';
import logger from '@/lib/logging';
import { detectLocaleUnit } from '@/lib/locale-unit';
import {
  fetchPostureSettings,
  savePostureSettingsHttp,
} from '@/components/posture-analysis/services/posture-settings-api';


export const DEFAULT_ADULT_HEIGHT_CM = 175;

// Default posture analysis settings
export const defaultPostureSettings: Settings = {
  unit: 'cm',
  clientHeightCM: DEFAULT_ADULT_HEIGHT_CM,
  postureOrientationStrategyId: DEFAULT_POSTURE_ORIENTATION_STRATEGY_ID,
};

const LS_STRATEGY_KEY = 'postureOrientationStrategyId';

/**
 * Loads posture analysis settings for a user
 * @param userId The authenticated user's ID
 * @returns The user's posture settings merged with defaults
 */
export async function loadPostureSettings(_userId: string): Promise<Settings> {
  try {
    const savedSettings = await fetchPostureSettings();
    return { ...defaultPostureSettings, ...savedSettings };
  } catch (error) {
    logger.error('Error loading posture settings from API:', error);
    return loadPostureSettingsFromLocalStorage();
  }
}

/**
 * Saves posture analysis settings to Firebase
 * Only saves if settings differ from defaults
 * @param userId The authenticated user's ID
 * @param settings The settings to save
 */
export async function savePostureSettings(_userId: string, settings: Settings): Promise<void> {
  try {
    await savePostureSettingsHttp(settings);
    savePostureSettingsToLocalStorage(settings);
  } catch (error) {
    logger.error('Error saving posture settings via API:', error);
    savePostureSettingsToLocalStorage(settings);
  }
}

/**
 * Loads posture settings from localStorage (fallback)
 */
export function loadPostureSettingsFromLocalStorage(): Settings {
  try {
    const savedUnit = localStorage.getItem('unit') as 'cm' | 'inches' | null;
    // Fall back to locale-derived unit when the user has never set a preference.
    const unit: Settings['unit'] = savedUnit ?? detectLocaleUnit();
    const clientHeightCM =
      parseFloat(localStorage.getItem('clientHeightCM') || '0') || defaultPostureSettings.clientHeightCM;
    const strategyRaw = localStorage.getItem(LS_STRATEGY_KEY);

    return {
      ...defaultPostureSettings,
      unit,
      clientHeightCM,
      postureOrientationStrategyId: strategyRaw
        ? (strategyRaw as Settings['postureOrientationStrategyId'])
        : defaultPostureSettings.postureOrientationStrategyId,
    };
  } catch (error) {
    logger.error('Error loading posture settings from localStorage:', error);
    return defaultPostureSettings;
  }
}

/**
 * Saves posture settings to localStorage (fallback or quick access)
 */
export function savePostureSettingsToLocalStorage(settings: Settings): void {
  try {
    localStorage.setItem('unit', settings.unit);
    if (settings.clientHeightCM > 0) {
      localStorage.setItem('clientHeightCM', settings.clientHeightCM.toString());
    } else {
      localStorage.removeItem('clientHeightCM');
    }
    const sid = settings.postureOrientationStrategyId ?? defaultPostureSettings.postureOrientationStrategyId;
    if (sid && sid !== defaultPostureSettings.postureOrientationStrategyId) {
      localStorage.setItem(LS_STRATEGY_KEY, sid);
    } else {
      localStorage.removeItem(LS_STRATEGY_KEY);
    }
  } catch (error) {
    logger.error('Error saving posture settings to localStorage:', error);
  }
}
